import { useState, useEffect } from 'react';
import { supabase, isSupabaseConfigured } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

interface Mission {
  id: string;
  title: string;
  description: string;
  category: string;
  difficulty: 'easy' | 'medium' | 'hard';
  points: number;
  time_frame: 'daily' | 'weekly' | 'monthly';
  verification_method: 'photo' | 'location' | 'quiz' | 'self-report';
  icon: string;
  trending: boolean;
  created_at: string;
  completed?: boolean;
  coming_soon?: boolean;
  launch_date?: string;
  active?: boolean;
  // New AR detection parameters
  ar_enabled?: boolean;
  target_objects?: string[];
  target_count?: number;
  // New permanent mission parameters
  is_permanent?: boolean;
  max_completions_per_day?: number;
  cooldown_hours?: number;
  // Completion tracking for permanent missions
  completions_today?: number;
  last_completed?: string;
  can_complete?: boolean;
  next_available?: string;
}

// Comprehensive mock missions that exactly match the database structure
const mockMissions: Mission[] = [
  {
    id: 'mission-1',
    title: 'Bring Your Own Bag',
    description: 'Use a reusable bag for shopping instead of plastic bags',
    category: 'Shopping',
    difficulty: 'easy',
    points: 50,
    time_frame: 'daily',
    verification_method: 'photo',
    icon: 'ShoppingBag',
    trending: true,
    created_at: '2024-12-08T00:00:00Z',
    completed: false,
    coming_soon: false,
    active: true,
    ar_enabled: true,
    target_objects: ['bag', 'shopping_bag', 'reusable_bag'],
    target_count: 1,
    is_permanent: true,
    max_completions_per_day: 5,
    cooldown_hours: 1,
    completions_today: 0,
    can_complete: true
  },
  {
    id: 'mission-2',
    title: '5-Minute Power Shower',
    description: 'Take a shower in 5 minutes or less to save water',
    category: 'Water',
    difficulty: 'medium',
    points: 75,
    time_frame: 'daily',
    verification_method: 'self-report',
    icon: 'Droplets',
    trending: false,
    created_at: '2024-12-08T00:00:00Z',
    completed: false,
    coming_soon: false,
    active: true,
    ar_enabled: false,
    is_permanent: false,
    max_completions_per_day: 1,
    cooldown_hours: 0,
    can_complete: true
  },
  {
    id: 'mission-3',
    title: 'Car-Free Commute',
    description: 'Walk, bike, or use public transport instead of driving',
    category: 'Transport',
    difficulty: 'medium',
    points: 100,
    time_frame: 'daily',
    verification_method: 'location',
    icon: 'Bike',
    trending: true,
    created_at: '2024-12-08T00:00:00Z',
    completed: false,
    coming_soon: false,
    active: true,
    ar_enabled: false,
    is_permanent: false,
    max_completions_per_day: 1,
    cooldown_hours: 0,
    can_complete: true
  },
  {
    id: 'mission-4',
    title: 'Plant-Based Meal',
    description: 'Share a photo of your delicious meat-free meal',
    category: 'Food',
    difficulty: 'easy',
    points: 60,
    time_frame: 'daily',
    verification_method: 'photo',
    icon: 'Leaf',
    trending: false,
    created_at: '2024-12-08T00:00:00Z',
    completed: false,
    coming_soon: false,
    active: true,
    ar_enabled: true,
    target_objects: ['salad', 'vegetables', 'fruit', 'plant_food'],
    target_count: 1,
    is_permanent: true,
    max_completions_per_day: 3,
    cooldown_hours: 2,
    completions_today: 0,
    can_complete: true
  },
  {
    id: 'mission-5',
    title: 'Community Clean-Up',
    description: 'Organize or join a local clean-up event with 5+ people',
    category: 'Community',
    difficulty: 'hard',
    points: 300,
    time_frame: 'monthly',
    verification_method: 'photo',
    icon: 'Users',
    trending: false,
    created_at: '2024-12-08T00:00:00Z',
    completed: false,
    coming_soon: false,
    active: true,
    ar_enabled: false,
    is_permanent: false,
    max_completions_per_day: 1,
    cooldown_hours: 0,
    can_complete: true
  },
  {
    id: 'mission-6',
    title: 'Plastic Bottle Collection',
    description: 'Collect and recycle plastic bottles - complete anytime you find bottles!',
    category: 'Waste',
    difficulty: 'medium',
    points: 150,
    time_frame: 'weekly',
    verification_method: 'photo',
    icon: 'Recycle',
    trending: false,
    created_at: '2024-12-08T00:00:00Z',
    completed: false,
    coming_soon: false,
    active: true,
    ar_enabled: true,
    target_objects: ['bottle', 'plastic_bottle', 'water_bottle'],
    target_count: 3,
    is_permanent: true,
    max_completions_per_day: 5,
    cooldown_hours: 1,
    completions_today: 0,
    can_complete: true
  }
];

// Helper function to check if error is a network/fetch error
const isNetworkError = (error: any): boolean => {
  return error instanceof TypeError && 
         (error.message.includes('Failed to fetch') || 
          error.message.includes('Network request failed') ||
          error.message.includes('fetch'));
};

export function useMissions() {
  const [missions, setMissions] = useState<Mission[]>([]);
  const [loading, setLoading] = useState(true);
  const { user, profile, updateProfile } = useAuth();

  useEffect(() => {
    fetchMissions();
  }, [user]);

  const fetchMissions = async () => {
    try {
      setLoading(true);
      
      if (!isSupabaseConfigured) {
        console.log('Using mock missions data - Supabase not configured');
        setMissions(mockMissions);
        setLoading(false);
        return;
      }

      console.log('Fetching missions from database...');
      
      // Fetch ALL missions with permanent mission fields
      const { data: missionsData, error: missionsError } = await supabase
        .from('missions')
        .select(`
          *,
          is_permanent,
          max_completions_per_day,
          cooldown_hours
        `)
        .order('created_at', { ascending: false });

      if (missionsError) {
        console.error('Error fetching missions:', missionsError);
        console.log('Falling back to mock missions data');
        setMissions(mockMissions);
        setLoading(false);
        return;
      }

      if (!missionsData || missionsData.length === 0) {
        console.log('No missions found in database, using mock data');
        setMissions(mockMissions);
        setLoading(false);
        return;
      }

      console.log(`Found ${missionsData.length} missions in database:`, missionsData.map(m => ({ 
        title: m.title, 
        active: m.active, 
        coming_soon: m.coming_soon,
        is_permanent: m.is_permanent,
        max_per_day: m.max_completions_per_day
      })));

      // If user is logged in, check completion status and permanent mission tracking
      if (user) {
        try {
          // Get all completions for the user
          const { data: completedData, error: completedError } = await supabase
            .from('completed_missions')
            .select('mission_id, completed_at')
            .eq('user_id', user.id)
            .order('completed_at', { ascending: false });

          if (completedError) {
            console.error('Error fetching completed missions:', completedError);
            setMissions(missionsData);
            setLoading(false);
            return;
          }

          const completions = completedData || [];
          const today = new Date().toISOString().split('T')[0];

          const missionsWithCompletion = missionsData.map(mission => {
            const missionCompletions = completions.filter(c => c.mission_id === mission.id);
            const todayCompletions = missionCompletions.filter(c => 
              c.completed_at.startsWith(today)
            );
            const lastCompletion = missionCompletions[0]; // Most recent

            let canComplete = true;
            let nextAvailable = '';

            if (mission.is_permanent) {
              // Check daily limit
              if (todayCompletions.length >= (mission.max_completions_per_day || 1)) {
                canComplete = false;
                nextAvailable = 'Tomorrow';
              }

              // Check cooldown
              if (canComplete && lastCompletion && mission.cooldown_hours > 0) {
                const lastCompletedTime = new Date(lastCompletion.completed_at);
                const cooldownEnd = new Date(lastCompletedTime.getTime() + (mission.cooldown_hours * 60 * 60 * 1000));
                const now = new Date();

                if (now < cooldownEnd) {
                  canComplete = false;
                  const timeLeft = Math.ceil((cooldownEnd.getTime() - now.getTime()) / (1000 * 60));
                  nextAvailable = timeLeft < 60 ? `${timeLeft}m` : `${Math.ceil(timeLeft / 60)}h`;
                }
              }

              return {
                ...mission,
                completed: false, // Permanent missions are never "completed"
                completions_today: todayCompletions.length,
                last_completed: lastCompletion?.completed_at,
                can_complete: canComplete,
                next_available: nextAvailable
              };
            } else {
              // One-time missions
              return {
                ...mission,
                completed: missionCompletions.length > 0,
                can_complete: missionCompletions.length === 0
              };
            }
          });

          console.log('Missions with completion status:', missionsWithCompletion.length);
          setMissions(missionsWithCompletion);
        } catch (completedError) {
          console.error('Network error fetching completed missions:', completedError);
          setMissions(missionsData);
        }
      } else {
        console.log('No user logged in, showing all missions without completion status');
        setMissions(missionsData.map(mission => ({
          ...mission,
          can_complete: true
        })));
      }
    } catch (error) {
      console.error('Error fetching missions:', error);
      console.log('Using mock missions data due to error');
      setMissions(mockMissions);
    } finally {
      setLoading(false);
    }
  };

  const completeMission = async (missionId: string, proofUrl?: string, completionData?: any) => {
    if (!user || !profile) return { error: 'User not authenticated' };

    try {
      console.log('🎯 Starting mission completion process...', {
        missionId,
        userId: user.id,
        hasCompletionData: !!completionData,
        isARMode: completionData?.arMode
      });

      if (!isSupabaseConfigured) {
        console.log('Mock mission completion');
        
        // Find the mission to get points
        const mission = missions.find(m => m.id === missionId);
        if (mission) {
          // Update local profile with new eco coins and points
          await updateProfile({
            eco_coins: profile.eco_coins + mission.points,
            points: (profile.points || 0) + mission.points
          });

          // Update mission completion status
          if (mission.is_permanent) {
            // For permanent missions, update completion tracking
            setMissions(prev => prev.map(m => 
              m.id === missionId ? { 
                ...m, 
                completions_today: (m.completions_today || 0) + 1,
                last_completed: new Date().toISOString()
              } : m
            ));
          } else {
            // For one-time missions, mark as completed
            setMissions(prev => prev.map(m => 
              m.id === missionId ? { ...m, completed: true, can_complete: false } : m
            ));
          }
        }
        
        return { error: null };
      }

      // Get mission details
      const { data: mission, error: missionError } = await supabase
        .from('missions')
        .select('*')
        .eq('id', missionId)
        .single();

      if (missionError) {
        console.error('Error fetching mission:', missionError);
        throw missionError;
      }

      console.log('✅ Mission found:', mission.title, 'Points:', mission.points, 'Permanent:', mission.is_permanent);

      // For permanent missions, check if user can complete it
      if (mission.is_permanent) {
        const { data: canCompleteResult, error: checkError } = await supabase
          .rpc('can_complete_permanent_mission', {
            p_user_id: user.id,
            p_mission_id: missionId
          });

        if (checkError) {
          console.error('Error checking permanent mission eligibility:', checkError);
          // Continue anyway for now
        } else if (!canCompleteResult) {
          return { error: 'Mission cannot be completed yet. Check daily limit or cooldown.' };
        }
      } else {
        // For one-time missions, check if already completed
        const { data: existingCompletion, error: checkError } = await supabase
          .from('completed_missions')
          .select('id')
          .eq('user_id', user.id)
          .eq('mission_id', missionId)
          .maybeSingle();

        if (checkError) {
          console.error('Error checking existing completion:', checkError);
          throw checkError;
        }

        if (existingCompletion) {
          console.log('One-time mission already completed');
          return { error: 'Mission already completed' };
        }
      }

      // 🔥 CRITICAL: Create detailed completion record FIRST if we have enhanced completion data
      if (completionData && completionData.arMode) {
        console.log('🚀 Creating enhanced AR mission completion record...');
        
        try {
          const { data: completionRecord, error: completionError } = await supabase
            .from('mission_completions')
            .insert({
              user_id: user.id,
              mission_id: missionId,
              completion_data: {
                detectedItems: completionData.detectedItems || 0,
                targetObjects: completionData.targetObjects || [],
                completionTime: completionData.completionTime || Date.now(),
                arMode: completionData.arMode || false,
                realDetection: completionData.realDetection || false,
                enhancedAction: completionData.enhancedAction || false,
                missionData: completionData.missionData || {},
                isPermanent: mission.is_permanent || false
              },
              detection_data: completionData.detectionData || [],
              action_photo: completionData.actionPhoto || null,
              confidence_score: completionData.confidence || 0,
              status: 'pending'
            })
            .select()
            .single();

          if (completionError) {
            console.error('❌ Error creating completion record:', completionError);
          } else {
            console.log('✅ Enhanced completion record created successfully:', completionRecord.id);
          }
        } catch (detailedError) {
          console.error('❌ Failed to create detailed completion record:', detailedError);
        }
      }

      // 🔥 CRITICAL: Record completion in the regular completed_missions table
      console.log('💾 Recording completion in completed_missions table...');
      
      const { data: regularCompletion, error: completionError } = await supabase
        .from('completed_missions')
        .insert({
          user_id: user.id,
          mission_id: missionId,
          points_earned: mission.points,
          proof_url: proofUrl || completionData?.actionPhoto,
          verified: true
        })
        .select()
        .single();

      if (completionError) {
        console.error('❌ Error recording completion:', completionError);
        throw completionError;
      }

      console.log('✅ Mission completion recorded successfully:', regularCompletion.id);

      // Update local profile state to reflect the changes
      console.log('🔄 Updating user profile with new points...');
      await updateProfile({
        eco_coins: profile.eco_coins + mission.points,
        points: (profile.points || 0) + mission.points
      });

      console.log('✅ Profile updated successfully');

      // Refresh missions to show updated completion status
      await fetchMissions();

      console.log('🎉 Mission completion process finished successfully!');
      return { error: null };
    } catch (error) {
      console.error('❌ Error completing mission:', error);
      return { error };
    }
  };

  return {
    missions,
    loading,
    completeMission,
    refetch: fetchMissions
  };
}